import * as angular from "angular";
import {IController} from "angular";
import {ImportVerification, MappingError} from "../../../commons/models/import-verification.model";
import {VisitConfigurationModel} from "../../models/visit-configuration.model";
import {
  VisitConfigurationCellEditField,
  VisitConfigurationCellEditPanelComponent
} from "../visit-configuration-cell-edit-panel/visit-configuration-cell-edit-panel.component";
import "../visit-configuration-cell-edit-panel/visit-configuration-cell-edit-panel.scss";

const NO_VALUE = '---';
const ACCOUNT_FIELDS = ['account', 'accountId'];
const CALL_TYPE_FIELDS = ['serviceType'];
const START_DATE_FIELDS = ['startRecurring', 'startTime'];
const END_DATE_FIELDS = ['endRecurring', 'endTime'];
const FREQ_FIELDS = ['freq', 'weekDay'];

type RecapRow = ImportVerification<VisitConfigurationModel> & {
  __pending?: boolean;
  status?: 'Valid' | 'Invalid' | 'Pending';
  accountDisplay?: string;
  accountErrors?: MappingError[];
  hasAccountError?: boolean;
  callTypeDisplay?: string;
  callTypeErrors?: MappingError[];
  hasServiceTypeError?: boolean;
  destinationDisplay?: string;
  cassesDisplay?: string;
  startDateDisplay?: string;
  startDateErrors?: MappingError[];
  hasStartDateError?: boolean;
  endDateDisplay?: string;
  endDateErrors?: MappingError[];
  hasEndDateError?: boolean;
  freqDisplay?: string;
  weekDaysDisplay?: string;
  freqErrors?: MappingError[];
  hasFreqError?: boolean;
  searchIndex?: string;
};

export class VisitConfigurationUploadRecapTableComponent implements IController {
  rows: RecapRow[];
  queryString: string;
  statusFilter: string;
  callTypeFilter: string;
  onDeleteRow: Function;
  onRowEdited: Function;

  filteredRows: RecapRow[] = [];

  private decoratedRows: RecapRow[] = [];

  /* @ngInject */
  constructor(private $mdPanel, private $filter) {
  }

  $onChanges(changes) {
    if (changes.rows) {
      this.decoratedRows = this.rows || [];
      this.decoratedRows.forEach(row => this._decorateRow(row));
    }
    this.filteredRows = this._computeFilteredRows();
  }

  onEditAccount($event, row: RecapRow) {
    this._openCellEditPanel($event, 'account', row, {
      id: row.entity?.accountId,
      name: row.entity?.accountReference?.name
    }, (result) => {
      const entity = this._ensureEntity(row);
      entity.accountId = result.id;
      entity.accountReference = result.id ? {id: result.id, name: result.name} as any : undefined;
    });
  }

  onEditCallType($event, row: RecapRow) {
    this._openCellEditPanel($event, 'callType', row, row.entity?.serviceType, (result) => {
      this._ensureEntity(row).serviceType = result;
    });
  }

  onEditDestination($event, row: RecapRow) {
    this._openCellEditPanel($event, 'destination', row, row.entity?.destination?.location?.address, (result) => {
      const entity = this._ensureEntity(row);
      entity.destination = entity.destination || ({} as any);
      entity.destination.location = entity.destination.location || ({} as any);
      entity.destination.location.address = result;
    });
  }

  onEditStartDate($event, row: RecapRow) {
    this._openCellEditPanel($event, 'startDate', row, row.entity?.schedule?.startRecurring, (result) => {
      const entity = this._ensureEntity(row);
      entity.schedule = entity.schedule || ({} as any);
      entity.schedule.startRecurring = result;
    });
  }

  onEditEndDate($event, row: RecapRow) {
    this._openCellEditPanel($event, 'endDate', row, row.entity?.schedule?.endRecurring, (result) => {
      const entity = this._ensureEntity(row);
      entity.schedule = entity.schedule || ({} as any);
      entity.schedule.endRecurring = result;
    });
  }

  onEditFrequency($event, row: RecapRow) {
    this._openCellEditPanel($event, 'frequency', row, {
      freq: row.entity?.schedule?.freq,
      byWeekDay: row.entity?.schedule?.byWeekDay
    }, (result) => {
      const entity = this._ensureEntity(row);
      entity.schedule = entity.schedule || ({} as any);
      entity.schedule.freq = result.freq;
      entity.schedule.byWeekDay = result.byWeekDay;
    });
  }

  private _decorateRow(row: RecapRow) {
    row.status = row.__pending ? 'Pending' : ((!row.mappingErrors || row.mappingErrors.length === 0) ? 'Valid' : 'Invalid');
    const errors = this._categorizeMappingErrors(row);

    const accountName = row.entity?.accountReference?.name || row.entity?.accountId;
    row.accountErrors = errors.accountErrors;
    row.hasAccountError = row.accountErrors.length > 0;
    row.accountDisplay = accountName ||
      (row.mappingErrors?.length ? row.mappingErrors.map(error => error.description).join('; ') : NO_VALUE);

    row.callTypeErrors = errors.callTypeErrors;
    row.hasServiceTypeError = row.callTypeErrors.length > 0;
    row.callTypeDisplay = row.entity?.serviceType ?
      this.$filter('translate')(`SERVICE_TYPE.${row.entity.serviceType}`) : NO_VALUE;

    row.destinationDisplay = this.$filter('bringozAddress')(row.entity?.destination?.location?.address, true) || NO_VALUE;

    const units = row.entity?.units;
    row.cassesDisplay = units === null || units === undefined ? NO_VALUE : String(units);

    row.startDateDisplay = this.$filter('bringozFullDate')(row.entity?.schedule, 'startRecurring', NO_VALUE);
    row.startDateErrors = errors.startDateErrors;
    row.hasStartDateError = row.startDateErrors.length > 0;

    row.endDateDisplay = this.$filter('bringozFullDate')(row.entity?.schedule, 'endRecurring', NO_VALUE);
    row.endDateErrors = errors.endDateErrors;
    row.hasEndDateError = row.endDateErrors.length > 0;

    const freq = row.entity?.schedule?.freq;
    row.freqDisplay = freq ? this.$filter('translate')(`RECURRING.RRule.${freq}`) : NO_VALUE;
    row.weekDaysDisplay = (row.entity?.schedule?.byWeekDay || [])
      .map(dayKey => this.$filter('translate')(`DAY_NAMES_SHORTCUT.${dayKey}`))
      .join(', ');
    row.freqErrors = errors.freqErrors;
    row.hasFreqError = row.freqErrors.length > 0;

    // Search matches only what the table renders, so a hit is always visible in a cell.
    row.searchIndex = [
      row.rowNumber,
      row.accountDisplay,
      row.callTypeDisplay,
      row.destinationDisplay,
      row.startDateDisplay,
      row.endDateDisplay,
      row.cassesDisplay,
      row.freqDisplay,
      row.weekDaysDisplay
    ].join(' ').toLowerCase();
  }

  private _categorizeMappingErrors(row: RecapRow) {
    const buckets = {
      accountErrors: [] as MappingError[],
      callTypeErrors: [] as MappingError[],
      startDateErrors: [] as MappingError[],
      endDateErrors: [] as MappingError[],
      freqErrors: [] as MappingError[]
    };

    for (const error of row.mappingErrors || []) {
      if (!error.fieldName) {
        continue;
      }

      if (ACCOUNT_FIELDS.includes(error.fieldName)) {
        buckets.accountErrors.push(error);
      } else if (CALL_TYPE_FIELDS.includes(error.fieldName)) {
        buckets.callTypeErrors.push(error);
      } else if (START_DATE_FIELDS.includes(error.fieldName)) {
        buckets.startDateErrors.push(error);
      } else if (END_DATE_FIELDS.includes(error.fieldName)) {
        buckets.endDateErrors.push(error);
      } else if (FREQ_FIELDS.includes(error.fieldName)) {
        buckets.freqErrors.push(error);
      }
    }

    return buckets;
  }

  private _computeFilteredRows(): RecapRow[] {
    const query = (this.queryString || '').trim().toLowerCase();

    return this.decoratedRows.filter((row: RecapRow) => {
      return (!query || row.searchIndex.includes(query)) &&
        (this.statusFilter === 'All' || row.status === this.statusFilter) &&
        (this.callTypeFilter === 'All' || row.entity?.serviceType === this.callTypeFilter);
    });
  }

  private _ensureEntity(row: RecapRow): VisitConfigurationModel {
    if (!row.entity) {
      row.entity = {} as VisitConfigurationModel;
    }
    return row.entity;
  }

  private _openCellEditPanel($event, field: VisitConfigurationCellEditField, row: RecapRow, value: any, applyResult: (result: any) => void) {
    const panelPosition = this.$mdPanel.newPanelPosition()
      .relativeTo($event.target)
      .addPanelPosition(this.$mdPanel.xPosition.CENTER, this.$mdPanel.yPosition.BELOW);

    const panelAnimation = this.$mdPanel.newPanelAnimation()
      .openFrom($event)
      .duration(200)
      .withAnimation(this.$mdPanel.animation.FADE);

    const config = {
      locals: {
        data: {field, rowNumber: row.rowNumber, value},
        onSubmit: (result) => {
          applyResult(result);
          row.__pending = true;
          this.onRowEdited({row});
        }
      },
      attachTo: angular.element(document.body),
      controller: VisitConfigurationCellEditPanelComponent,
      controllerAs: 'ctrl',
      position: panelPosition,
      animation: panelAnimation,
      targetEvent: $event,
      template: require('../visit-configuration-cell-edit-panel/visit-configuration-cell-edit-panel.html').default,
      clickOutsideToClose: true,
      escapeToClose: true,
      focusOnOpen: true,
      zIndex: 80
    };

    this.$mdPanel.open(config);
  }
}

angular
  .module('bringoz.admin.scheduling-project')
  .component('visitConfigurationUploadRecapTable', {
    bindings: {
      rows: '<',
      queryString: '<',
      statusFilter: '<',
      callTypeFilter: '<',
      onDeleteRow: '&',
      onRowEdited: '&'
    },
    template: require('./visit-configuration-upload-recap-table.html').default,
    controller: VisitConfigurationUploadRecapTableComponent
  });
